// Answers "is there queued offline work?" outside every React provider.
//
// `useSyncStatus()` throws outside `SyncStatusProvider`, which only mounts once
// the query cache is restored — exactly the boot states the PWA layer must be
// readable in. This module reads the provider-free `lib/offlineQueue` instead,
// and because that queue lives in IndexedDB the snapshot also sees work queued
// by another document. Mirrors the subscribe/getSnapshot idiom of
// `lib/offline/reachability.ts`.

import { listAllQueueItems, subscribeToQueue } from "@/lib/offlineQueue";
import type { PendingWork } from "./updateApplyPolicy";

type Listener = () => void;

let pendingWork: PendingWork = "unknown";
const listeners = new Set<Listener>();

// Bumped by every queue notification. `notifyQueueChanged()` fires *after* the
// IndexedDB write lands, so a read that spans no bump observed a quiescent store.
let changeGeneration = 0;
// Last-started-wins for background refreshes: two reads can resolve out of start
// order and the older one must not write a stale verdict over the newer one.
let readGeneration = 0;

let activeDisposer: (() => void) | null = null;

function notify() {
	for (const fn of listeners) fn();
}

function setPendingWork(next: PendingWork) {
	if (next === pendingWork) return;
	pendingWork = next;
	notify();
}

function toPendingWork(depth: number): PendingWork {
	return depth > 0 ? "busy" : "idle";
}

async function refresh(): Promise<void> {
	const generation = ++readGeneration;
	const items = await listAllQueueItems();
	if (generation !== readGeneration) return;
	setPendingWork(toPendingWork(items.length));
}

function onQueueChanged() {
	changeGeneration += 1;
	void refresh();
}

/**
 * Subscribe to the offline queue and take a first reading. Idempotent — calling
 * it again returns the same disposer rather than opening a second subscription.
 */
export function startPendingWorkSnapshot(): () => void {
	if (activeDisposer) return activeDisposer;

	const unsubscribeQueue = subscribeToQueue(onQueueChanged);
	const dispose = () => {
		if (activeDisposer !== dispose) return;
		activeDisposer = null;
		unsubscribeQueue();
	};
	activeDisposer = dispose;

	void refresh();
	return dispose;
}

export function getPendingWork(): PendingWork {
	return pendingWork;
}

export function subscribePendingWork(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

// WHY a cap and not an open loop: a sustained burst of queue writes — a replay
// flushing many items, each notifying after its own write — can invalidate every
// read forever, and an unbounded retry would then never resolve, so the reload
// arbiter awaiting it would never reload and the update would silently never
// apply. Ten reads is chosen over a wall-clock deadline because the loop's unit
// of progress is a read, not elapsed time: it makes the bound deterministic
// under fake timers and paused-IndexedDB test doubles, and ten consecutive
// contested reads is no longer a transient race — it is sustained write traffic,
// which is itself the "there is pending work" answer.
const MAX_SETTLE_READS = 10;

/**
 * Resolves once every in-flight queue write has landed in IndexedDB.
 *
 * WHY the loop: `enqueue`/`removeQueueItem`/`updateQueueItem`/`clearQueue` are
 * async read-modify-write cycles that call `notifyQueueChanged()` only *after*
 * their write resolves. So a `listAllQueueItems()` read that completes with no
 * notification racing it is proof that no write was mid-flight when it ran. If a
 * notification did arrive while the read was in flight, the read may have
 * observed the pre-write state — re-read. The loop ends on the first uncontested
 * read, which is the value the reload arbiter is allowed to act on — that
 * property is unchanged, and the uncontested case still returns on read one.
 *
 * On exhaustion it resolves rather than hanging. An exhausted loop never proved
 * the store quiescent, so it reports what it last saw, clamped toward safety: a
 * contested `busy` is truthful and keeps the caller deferring, while a contested
 * `idle` could be the pre-write state of a landing `enqueue`, so it degrades to
 * `unknown` — which the apply policy also defers on. Exhaustion can therefore
 * never newly permit a reload. The module snapshot is deliberately left
 * untouched in that path: this is a verdict for one caller, not an observation.
 */
export async function awaitPendingWorkSettled(): Promise<PendingWork> {
	let lastObserved: PendingWork = "unknown";

	for (let read = 0; read < MAX_SETTLE_READS; read++) {
		const changeAtStart = changeGeneration;
		const generation = ++readGeneration;
		const items = await listAllQueueItems();
		lastObserved = toPendingWork(items.length);
		if (changeGeneration !== changeAtStart) continue;

		if (generation === readGeneration) setPendingWork(lastObserved);
		return lastObserved;
	}

	return lastObserved === "busy" ? "busy" : "unknown";
}

// Exposed for unit tests only — allows resetting module state between test cases.
export function _resetForTesting() {
	activeDisposer?.();
	pendingWork = "unknown";
	changeGeneration = 0;
	readGeneration += 1;
	listeners.clear();
}
